// 購入経路の4項目（cartRoute / entryRoute / basedOnUserDesign / fromShareUrl）を、
// 商品の購入シナリオから決めるロジックをこのファイルに集約する。
// 各コンポーネントが「CART_ROUTE のどれを使うか」「entryRoute の穴埋め」「再デザイン時のマージ」を
// 個別に書かずに済むようにし、経路のルールを1箇所に保つ。

import { CART_ROUTE, type CartRoute, type PurchaseRoute, type PurchaseRouteFields, isCartRoute } from '@/constants';
import { hasShareOrigin } from '@/utils/shareOrigin';

// 経路を新規に決めるシナリオ。新ルートはここに1ケース足すのが唯一の変更点。
export type PurchaseScenario =
  // 定型・みんなのデザイン
  | { kind: 'fixedDesign'; basedOnUserDesign: boolean }
  // テンプレ商品（アクスタ/シール）
  | { kind: 'template' }
  // デザインツール
  | { kind: 'design'; entryRouteParam: string | null; basedOnUserDesign: boolean };

/**
 * 購入シナリオから経路4項目（cartRoute / entryRoute / basedOnUserDesign / fromShareUrl）を新規に決める。
 * 経路ルールの単一の決定点。新しい入口が増えたら PurchaseScenario に1ケース足してここに分岐を追加する。
 * fromShareUrl は呼び出し時の URL（共有経由か）から自動で埋めるため、呼び出し側で渡す必要はない。
 * @param scenario - どの購入入口から来たか。design の場合は URL クエリの entryRoute も含む
 * @returns カート投入・計測で使う確定済みの経路。design は cartRoute が常に designed、entryRoute は入口を継承（不正/不在なら designed）
 */
export const resolvePurchaseRoute = (scenario: PurchaseScenario): PurchaseRoute => {
  const fromShareUrl = hasShareOrigin();
  switch (scenario.kind) {
    case 'fixedDesign':
      return {
        cartRoute: CART_ROUTE.fixedDesign,
        entryRoute: CART_ROUTE.fixedDesign,
        basedOnUserDesign: scenario.basedOnUserDesign,
        fromShareUrl,
      };
    case 'template':
      return {
        cartRoute: CART_ROUTE.template,
        entryRoute: CART_ROUTE.template,
        basedOnUserDesign: false,
        fromShareUrl,
      };
    case 'design': {
      // cartRoute はツール投入で常に designed。entryRoute は入口を継承し、不正/不在なら designed
      const entryRoute = isCartRoute(scenario.entryRouteParam) ? scenario.entryRouteParam : CART_ROUTE.designed;
      return {
        cartRoute: CART_ROUTE.designed,
        entryRoute,
        basedOnUserDesign: scenario.basedOnUserDesign,
        fromShareUrl,
      };
    }
  }
};

/**
 * 再デザイン時に、既存カート行の経路(previous)と今回の経路(next)を1行分にまとめる。
 * API 側の保存ロジックと必ず同じ結果になるよう、マージ規則をここに固定する（ズレると計測と保存値が食い違う）。
 *  - cartRoute        : 今回の値（designed）で上書き
 *  - entryRoute       : 最初にカートへ入れたときの入口を残す（既存があれば優先、無ければ今回の値）
 *  - basedOnUserDesign / fromShareUrl : 既存か今回のどちらかが true なら true（OR）
 * @param previous - 既存カート行が持つ経路。新規行で履歴が無ければ undefined
 * @param next - 今回のデザイン操作で決まった経路
 * @returns マージ後の経路
 */
export const mergeRedesignRoute = (previous: PurchaseRouteFields | undefined, next: PurchaseRoute): PurchaseRoute => ({
  cartRoute: CART_ROUTE.designed,
  entryRoute: previous?.entryRoute ?? next.entryRoute,
  basedOnUserDesign: (previous?.basedOnUserDesign ?? false) || next.basedOnUserDesign,
  fromShareUrl: (previous?.fromShareUrl ?? false) || next.fromShareUrl,
});

// 経路が解決できない明細の cartRoute / entryRoute に入れる値。PostHog のスカラー breakdown で null を出さないためのフォールバック
const UNKNOWN_ROUTE = 'unknown';

/**
 * 明細1件分の経路を、PostHog イベントのスカラープロパティに写す。
 * add_to_cart（PostHog には常に1商品で送る）と purchase_item（明細ごとに1件）で共有する。
 * 経路が解決できないときは cartRoute / entryRoute を 'unknown'、フラグを false にする。
 * @param route - 明細の経路（一部未確定なら undefined を含む）
 * @returns PostHog プロパティ用の経路4項目（camelCase・スカラー）
 */
export const toPostHogRoute = (route: PurchaseRouteFields) => ({
  cartRoute: route.cartRoute ?? UNKNOWN_ROUTE,
  entryRoute: route.entryRoute ?? UNKNOWN_ROUTE,
  basedOnUserDesign: route.basedOnUserDesign ?? false,
  fromShareUrl: route.fromShareUrl ?? false,
});

/**
 * 経路を API へ送る際の snake_case 変換。FE は camelCase、API パラメータは snake_case のため境界でここを通す。
 * @param route - FE 側（camelCase）の経路
 * @returns API リクエスト用（snake_case）の経路
 */
export const toSnakeCaseRoute = (route: PurchaseRouteFields) => ({
  cart_route: route.cartRoute,
  entry_route: route.entryRoute,
  based_on_user_design: route.basedOnUserDesign,
  from_share_url: route.fromShareUrl,
});

/**
 * デザイン編集へ遷移する /design? のクエリを組み立てる。
 * URL クエリは文字列しか持てないため boolean を文字列化する。受け側は必ず `=== 'true'` で復元する想定で、
 * その文字列化をこの1箇所に閉じておく（生成と復元の表記がズレると常に false 扱いになる罠を防ぐ）。
 * @param entryRoute - 遷移元の入口経路。design 側で entryRoute として継承される
 * @param basedOnUserDesign - みんなのデザインを下敷きにしているか
 * @returns /design? のクエリオブジェクト（basedOnUserDesign は 'true' / 'false' の文字列）
 */
export const buildDesignEntryQuery = (entryRoute: CartRoute, basedOnUserDesign: boolean) => ({
  entryRoute,
  basedOnUserDesign: String(basedOnUserDesign),
});
